/**
 * ComplianceAuditDeskView.tsx
 *
 * Jurisdictional Fiscal Compliance & URA EFRIS Audit Desk (Phase INV-02F):
 * - Real-time stream of fiscal clearance decisions and URA submissions.
 * - Offline buffer status monitor & manual sync trigger.
 * - Dead-Letter Queue (DLQ) review and retry console.
 * - URA FDN, Anti-Fake verification codes, and official QR code verification preview.
 */

import React, { useState } from 'react';
import type { ComplianceDecision, SubmissionResult } from '../../plugins/invoicing/compliance/compliance-contracts.js';
import type { QueuedRetryItem } from '../../plugins/invoicing/compliance/evidence/compliance-reconciler.js';

export interface ComplianceAuditDeskViewProps {
  readonly decisions: readonly ComplianceDecision[];
  readonly pendingRetryItems: readonly QueuedRetryItem[];
  readonly deadLetterItems: readonly QueuedRetryItem[];
  readonly activeJurisdiction?: string;
  readonly onProcessRetry: (queueId: string) => Promise<void>;
  readonly onOpenConfigModal: () => void;
}

export const ComplianceAuditDeskView: React.FC<ComplianceAuditDeskViewProps> = ({
  decisions,
  pendingRetryItems,
  deadLetterItems,
  activeJurisdiction = 'GLOBAL',
  onProcessRetry,
  onOpenConfigModal,
}) => {
  const [activeTab, setActiveTab] = useState<'decisions' | 'retries' | 'dlq'>('decisions');
  const [selectedDecision, setSelectedDecision] = useState<ComplianceDecision | null>(null);

  const isUganda = activeJurisdiction === 'UG';
  const isMalaysia = activeJurisdiction === 'MY';

  const deskTitle = isUganda
    ? 'URA EFRIS Compliance Desk'
    : isMalaysia
    ? 'LHDN MyInvois Compliance Desk'
    : 'Commercial Invoicing & Compliance Desk';

  const deskSubtitle = isUganda
    ? 'Zero-Trust Fiscal Clearance, Offline Buffer & Anti-Fake Evidence'
    : isMalaysia
    ? 'UBL 2.1 e-Invoice Validation, Offline Grace Window & LHDN Reconciliation'
    : 'Standard Commercial Invoicing, Tax Operations & General Ledger';

  const setupBtnLabel = isUganda
    ? '⚙️ EFRIS Setup'
    : isMalaysia
    ? '⚙️ MyInvois Setup'
    : '⚙️ Tax Rules Setup';

  return (
    <div className="flex flex-col h-full bg-slate-950 text-slate-100 p-6 space-y-6 font-mono select-none overflow-y-auto">
      {/* Operations Header */}
      <header className="flex justify-between items-center bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl">
        <div>
          <span className="text-xs uppercase font-bold text-amber-400 tracking-widest">
            {isUganda ? 'Uganda URA Authority' : isMalaysia ? 'Malaysia LHDN Authority' : 'Standard Commercial Desk'}
          </span>
          <h1 className="text-2xl font-black text-white mt-1">{deskTitle}</h1>
          <p className="text-xs text-slate-400">{deskSubtitle}</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="bg-slate-950 px-4 py-2 rounded-xl border border-slate-800 text-center">
            <span className="text-[10px] text-slate-400 uppercase font-bold">Decisions</span>
            <div className="text-lg font-black text-emerald-400">{decisions.length}</div>
          </div>
          <div className="bg-slate-950 px-4 py-2 rounded-xl border border-slate-800 text-center">
            <span className="text-[10px] text-slate-400 uppercase font-bold">DLQ Escalated</span>
            <div className="text-lg font-black text-rose-400">{deadLetterItems.length}</div>
          </div>
          <button
            onClick={onOpenConfigModal}
            className="px-4 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs border border-slate-700"
          >
            {setupBtnLabel}
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-800 pb-2 text-xs font-bold">
        <button
          onClick={() => setActiveTab('decisions')}
          className={`px-4 py-2 rounded-xl transition-all ${
            activeTab === 'decisions' ? 'bg-amber-400 text-slate-950' : 'bg-slate-900 text-slate-400 hover:text-white'
          }`}
        >
          Compliance Decisions ({decisions.length})
        </button>
        <button
          onClick={() => setActiveTab('retries')}
          className={`px-4 py-2 rounded-xl transition-all ${
            activeTab === 'retries' ? 'bg-amber-400 text-slate-950' : 'bg-slate-900 text-slate-400 hover:text-white'
          }`}
        >
          Pending Retries ({pendingRetryItems.length})
        </button>
        <button
          onClick={() => setActiveTab('dlq')}
          className={`px-4 py-2 rounded-xl transition-all ${
            activeTab === 'dlq' ? 'bg-rose-500 text-white' : 'bg-slate-900 text-slate-400 hover:text-white'
          }`}
        >
          Dead-Letter Queue ({deadLetterItems.length})
        </button>
      </div>

      {/* Tab Content */}
      <main className="space-y-4">
        {activeTab === 'decisions' && (
          <div className="space-y-3">
            {decisions.length === 0 ? (
              <div className="text-center p-12 bg-slate-900 border border-slate-800 rounded-2xl text-slate-500 text-xs">
                No compliance decisions recorded yet.
              </div>
            ) : (
              decisions.map((dec) => (
                <div
                  key={dec.decisionId}
                  onClick={() => setSelectedDecision(dec)}
                  className="bg-slate-900 hover:bg-slate-850 border border-slate-800 rounded-2xl p-5 flex justify-between items-center cursor-pointer transition-all text-xs"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-white text-sm">{dec.documentId}</span>
                      <span className="text-[10px] text-slate-400 uppercase">({dec.documentType})</span>
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                          dec.outcome === 'accepted' || dec.outcome === 'allowed'
                            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                            : dec.outcome === 'review'
                            ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                            : 'bg-rose-500/20 text-rose-300 border border-rose-500/40'
                        }`}
                      >
                        {dec.outcome}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">
                      Jurisdiction: {dec.jurisdiction} • Profile: {dec.profileVersion} • RuleSet: {dec.ruleSetVersion}
                    </p>
                  </div>

                  <div className="text-right text-[11px] text-slate-400 font-mono">
                    {new Date(dec.determinedAt).toLocaleTimeString()}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'retries' && (
          <div className="space-y-3">
            {pendingRetryItems.length === 0 ? (
              <div className="text-center p-12 bg-slate-900 border border-slate-800 rounded-2xl text-slate-500 text-xs">
                No pending retries in queue. All submissions reconciled.
              </div>
            ) : (
              pendingRetryItems.map((item) => (
                <div key={item.queueId} className="bg-slate-900 border border-slate-800 rounded-2xl p-5 space-y-3 text-xs">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-bold text-white">{item.documentId}</h4>
                      <p className="text-[11px] text-amber-400 mt-0.5">Status: {item.status} (Attempt {item.attemptCount}/{item.maxAttempts})</p>
                      {item.lastError && <p className="text-[11px] text-rose-300 mt-1">Error: {item.lastError}</p>}
                    </div>

                    <button
                      onClick={() => onProcessRetry(item.queueId)}
                      className="px-4 py-2 rounded-xl bg-amber-400 hover:bg-amber-300 text-slate-950 font-bold"
                    >
                      Retry Now
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'dlq' && (
          <div className="space-y-3">
            {deadLetterItems.length === 0 ? (
              <div className="text-center p-12 bg-slate-900 border border-slate-800 rounded-2xl text-slate-500 text-xs">
                Dead-Letter Queue is empty. Zero un-reconciled clearance rejections.
              </div>
            ) : (
              deadLetterItems.map((item) => (
                <div key={item.queueId} className="bg-rose-950/40 border border-rose-900/60 rounded-2xl p-5 space-y-3 text-xs">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-rose-600 text-white uppercase">DLQ</span>
                        <h4 className="font-bold text-white text-sm">{item.documentId}</h4>
                      </div>
                      <p className="text-[11px] text-rose-200 mt-1">{item.lastError || 'Max retry attempts exhausted'}</p>
                    </div>

                    <button
                      onClick={() => onProcessRetry(item.queueId)}
                      className="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-bold"
                    >
                      Manual Override
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
};
